<template>
  <section class="about-intro">
    <div class="container">
      <div class="intro-grid">
        <!-- VIDEO -->
        <div class="video-outer">
          <div class="video-wrapper" ref="videoWrapper">
            <video
              ref="video"
              class="intro-video"
              muted
              loop
              autoplay
              playsinline
              preload="none"
              :poster="videoPoster"
            >
              <source :src="videoSrc" type="video/mp4" />
            </video>

            <!-- industrial soft overlay -->
            <div class="video-overlay"></div>
          </div>
        </div>

        <!-- TEXT -->
        <div class="text-wrapper">
          <h2 class="intro-title">SHIXIAO MACHINERY</h2>

          <h3 class="intro-subtitle">
            Machines Built on Standardized, High-Precision Manufacturing
            Platforms
          </h3>

          <p class="intro-text">
            Powered by exceptional in-house manufacturing capabilities, SHIXIAO
            is the <strong>only abrasive machinery manufacturer</strong>
            equipped with large-scale, high-precision machining centers.
          </p>

          <p class="intro-text">
            From steel plate cutting to high-tolerance structural components, we
            have achieved
            <strong>fully standardized and self-controlled production</strong>
            of complete machine structures — ensuring long-term accuracy,
            stability and industrial reliability.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { aboutAssets } from "@/config/about";

export default {
  name: "AboutIntro",

  data() {
    return {
      windowWidth: window.innerWidth,
      observer: null,
    };
  },

  computed: {
    isMobile() {
      return this.windowWidth <= 768;
    },

    /** 🎬 视频源（只剩 mp4，最终上线形态） */
    videoSrc() {
      return this.isMobile
        ? aboutAssets.introVideo.mobile
        : aboutAssets.introVideo.desktop;
    },

    /** 🖼 封面 */
    videoPoster() {
      return this.isMobile
        ? aboutAssets.introVideo.posterMobile
        : aboutAssets.introVideo.posterDesktop;
    },
  },

  mounted() {
    window.addEventListener("resize", this.onResize);
    this.initObserver();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.observer) this.observer.disconnect();
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },

    /** 👁 进入视口才播放 */
    initObserver() {
      const video = this.$refs.video;
      const wrapper = this.$refs.videoWrapper;
      if (!video || !wrapper) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.35 }
      );

      this.observer.observe(wrapper);
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";

/* SECTION */
.about-intro {
  margin: 0 1rem;
  padding: 28px 1rem;
  background: linear-gradient(
    180deg,
    fade(@color-bg-light, 6%),
    @color-bg-main
  );

  @media (max-width: 768px) {
    margin: 0;
  }
}

/* GRID */
.intro-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 40px;
  align-items: center;
  overflow-x: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
}

/* VIDEO */
.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(16, 24, 32, 0.28);

  @media (max-width: 768px) {
    aspect-ratio: 4 / 5;
  }
}

.intro-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.95) contrast(1.06) saturate(0.92);
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.32));
  pointer-events: none;
}

/* TEXT */
.text-wrapper {
  color: @color-text-title;

  @media (max-width: 768px) {
    padding: 0 1.1rem;
  }
}

.intro-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.8px;
  margin-bottom: 10px;
}

.intro-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: @color-primary;
  line-height: 1.45;
  margin-bottom: 14px;
}

.intro-text {
  font-size: 15px;
  line-height: 1.75;
  color: @color-text-sub;
  margin-bottom: 12px;

  strong {
    color: @color-text-title;
    font-weight: 600;
  }
}
</style>
